import { FeedbackError } from '@/components/Feedback';
import config from '@/Constants';
import { useAuth } from '@/hooks/Authentication/auth';
import {
  Collection,
  CollectionItem,
} from '@/modules/core/domain/interfaces/ICollection';
import { ManageToken } from '@/modules/security/adapters';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import snakeToCamel from '@/utils/snakeToCamel';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import { useQuery } from 'react-query';

import { CollectionItemInputProps } from './CollectionItemInput.types';
import CollectionsList from './components/CollectionsList';
import ItemsList from './components/ItemsList';

interface PropertyValueProps {
  propertyKey: string;
  value: unknown;
}

const PropertyValue: React.FC<PropertyValueProps> = ({ value }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (value === null || value === undefined) {
    return (
      <Tag size="md" colorScheme="gray" borderRadius="md">
        <TagLabel fontSize="13px">N/A</TagLabel>
      </Tag>
    );
  }

  const stringValue = String(value);
  const MAX_LENGTH = 80;
  const isTruncated = stringValue.length > MAX_LENGTH;

  let displayValue: string;
  if (isExpanded) {
    displayValue = stringValue;
  } else if (isTruncated) {
    displayValue = `${stringValue.substring(0, MAX_LENGTH)}...`;
  } else {
    displayValue = stringValue;
  }

  // Determinar cor baseado no tipo de valor
  const getColorScheme = () => {
    if (typeof value === 'boolean') return value ? 'green' : 'red';
    if (typeof value === 'number') return 'purple';
    if (stringValue.match(/^\d{4}-\d{2}-\d{2}/)) return 'orange'; // Data
    return 'gray';
  };

  const getBooleanLabel = (val: boolean) => (val ? t('common.yes') : t('common.no'));

  return (
    <Box>
      <Tag
        size="md"
        colorScheme={getColorScheme()}
        borderRadius="md"
        maxW="100%"
      >
        <TagLabel
          fontSize="13px"
          whiteSpace={isExpanded ? 'normal' : 'nowrap'}
          overflow={isExpanded ? 'visible' : 'hidden'}
          textOverflow="ellipsis"
          maxW={isExpanded ? 'none' : '300px'}
        >
          {typeof value === 'boolean' ? getBooleanLabel(value) : displayValue}
        </TagLabel>
      </Tag>
      {isTruncated && (
        <Button
          variant="link"
          fontSize="xs"
          color="blue.500"
          fontWeight="semibold"
          ml={2}
          onClick={() => setIsExpanded(!isExpanded)}
          h="auto"
          minW="auto"
          _hover={{ textDecoration: 'underline' }}
        >
          {isExpanded ? t('common.see_less') : t('common.see_more')}
        </Button>
      )}
    </Box>
  );
};

const CollectionItemInput: React.FC<CollectionItemInputProps> = ({
  onChange,
  initialItem,
  queryKey,
  questionId,
}) => {
  const { t } = useTranslation();
  const [prevItem, setPrevItem] = React.useState(initialItem);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [collection, setCollection] = React.useState<Collection | undefined>();
  const [collections, setCollections] = React.useState<Collection[] | []>();
  const [item, setItem] = React.useState<CollectionItem | undefined>();
  const [fullInitialItem, setFullInitialItem] = React.useState<
    CollectionItem | undefined
  >();

  const { user } = useAuth();

  // Fetch full initial item data if initialItem exists
  const fetchInitialItem = React.useCallback(async () => {
    if (!initialItem?.id) return undefined;

    const queryParams = [`filter[collection_items][id]=${initialItem.id}`]
      .filter(Boolean)
      .join('&');

    const requestUrl = !user
      ? `external/external_${config.COLLECTION_ITEMS_URL}?${queryParams}`
      : `${config.COLLECTION_ITEMS_URL}?${queryParams}`;

    const unauthenticatedUser = !user;

    try {
      if (unauthenticatedUser) {
        ManageToken.set(config.REPORTS_API_AUTHORIZATION_TOKEN || '');
      }

      const response = await AxiosHelper.get<{
        items: CollectionItem[];
      }>({
        url: requestUrl,
      });

      if (response.statusCode !== 200 || !response.body.items[0]) {
        return undefined;
      }

      return snakeToCamel(response.body.items[0]);
    } catch {
      return undefined;
    } finally {
      if (unauthenticatedUser) {
        ManageToken.clear();
      }
    }
  }, [initialItem?.id, user]);

  const { data: fetchedInitialItem } = useQuery(
    ['collectionItem', initialItem?.id],
    fetchInitialItem,
    {
      enabled: !!initialItem?.id && !fullInitialItem,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  React.useEffect(() => {
    if (fetchedInitialItem) {
      setFullInitialItem(fetchedInitialItem);
    }
  }, [fetchedInitialItem]);

  const headerText = React.useMemo(() => {
    if (item && collection) {
      return `${collection.name} > ${item.name}`;
    }
    if (collection) {
      return `${collection.name} > ${t('common.select_item')}`;
    }
    return t('common.select_collection');
  }, [collection, item]);

  const handleDeselectPrev = React.useCallback(() => {
    setPrevItem(undefined);
    setFullInitialItem(undefined);
    onChange(undefined);
  }, [onChange]);

  const handleSave = React.useCallback(() => {
    if (item && collection) {
      onChange({ item, collection });
      onClose();
    } else {
      FeedbackError({ mainText: t('common.select_item_first') });
    }
  }, [onChange, item, collection, onClose]);

  const handleSelectItem = React.useCallback(
    (item: CollectionItem) => {
      if (!collection) {
        const itemCollection = collections?.find(
          (e) => e.id === item.collectionId,
        );
        itemCollection && setCollection(itemCollection);
      }
      setItem(item);
    },
    [collections, collection, setCollection, setItem],
  );

  const itemProperties = React.useMemo(() => {
    // Use fullInitialItem if available, otherwise use item or prevItem
    const currentItem = item || fullInitialItem || prevItem;
    if (!currentItem) return [];

    const fixedProps = ['id', 'name', 'code', 'active', 'collectionId'];
    return Object.entries(currentItem)
      .filter(([key]) => !fixedProps.includes(key))
      .map(([key, value]) => ({ key, value }));
  }, [item, fullInitialItem, prevItem]);

  return (
    <>
      <VStack align="stretch" spacing={3}>
        {prevItem && !item ? (
          <Button
            onClick={handleDeselectPrev}
            rightIcon={<Icon as={FiX} />}
            colorScheme="red"
          >
            {prevItem.name}
          </Button>
        ) : (
          <Button
            onClick={onOpen}
            colorScheme={item && initialItem ? 'orange' : 'brand'}
          >
            {item && initialItem ? item.name : t('common.select_collection_item')}
          </Button>
        )}

        {(item || fullInitialItem || prevItem) && (
          <Box
            p={3}
            borderWidth="1px"
            borderRadius="md"
            borderColor="gray.200"
            bg="white"
          >
            <VStack align="stretch" spacing={3}>
              {/* Código */}
              <Flex align="center" gap={2}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                  {t('common.code')}:
                </Text>
                <Tag size="sm" colorScheme="blue" borderRadius="md">
                  <TagLabel>
                    {(item || fullInitialItem || prevItem)?.code}
                  </TagLabel>
                </Tag>
              </Flex>

              {/* Propriedades customizadas */}
              {itemProperties.length > 0 && (
                <Wrap spacing={2}>
                  {itemProperties.map(({ key, value }) => (
                    <WrapItem key={key}>
                      <PropertyValue propertyKey={key} value={value} />
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </VStack>
          </Box>
        )}
      </VStack>

      <Modal size="3xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{headerText}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack alignItems="start" w="full">
              <CollectionsList
                queryKey={queryKey}
                selected={collection}
                onSelect={(collection) => {
                  setCollection(collection);
                  setItem(undefined);
                }}
                setCollections={setCollections}
              />
              <ItemsList
                collectionId={collection ? collection.id : undefined}
                questionId={questionId}
                selected={item}
                onSelect={handleSelectItem}
              />
            </HStack>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button onClick={handleSave} colorScheme="brand">
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CollectionItemInput;
